import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectCartItems } from '../../selectors/cart';
import { selectMenuModuleCartErrors } from '../../selectors/order';

/**
 * Order-level summary of the failing items/modifiers from the last menu-module
 * order failure. Failures that couldn't be resolved to cart nodes (unknown
 * error_type / unparseable detail) arrive as a single path-less error and get
 * a generic title with the raw message; renders nothing when there are no
 * errors at all.
 */
export default function OrderErrorSummary() {
  const cartErrors = useSelector(selectMenuModuleCartErrors);
  const cartItems = useSelector(selectCartItems);
  const [dismissed, setDismissed] = useState(false);

  // Re-show the banner whenever a new set of errors arrives (a fresh failure),
  // even if the previous one was dismissed.
  useEffect(() => {
    setDismissed(false);
  }, [cartErrors]);

  // An empty cart has nothing the errors could refer to (e.g. the operator
  // cleared it after a failure) — hide the banner rather than pointing at
  // items that no longer exist.
  const isCartEmpty = Object.keys(cartItems || {}).length === 0;

  if (!cartErrors.length || isCartEmpty || dismissed) {
    return null;
  }

  const nodeErrorCount = cartErrors.filter(
    (cartError) => cartError.nodePath.length
  ).length;
  const title =
    nodeErrorCount === 0
      ? "The order couldn't be sent"
      : nodeErrorCount === 1
      ? "1 item couldn't be added"
      : `${nodeErrorCount} items couldn't be added`;

  return (
    <Alert
      severity="error"
      onClose={() => setDismissed(true)}
      sx={{ marginBottom: '8px' }}
    >
      <AlertTitle>{title}</AlertTitle>
      <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
        {/* Raw diagnostics: error code + exact path through the cart
            hierarchy. The friendly message is already shown on the cart line
            itself; only path-less unknown errors (no cart line to speak of)
            show their message here. */}
        {cartErrors.map((cartError) => (
          <li key={cartError.nodePath.join(' > ') || cartError.message}>
            {cartError.nodePath.length > 0 ? (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                }}
              >
                {cartError.reasonCode}: {cartError.nodePath.join(' › ')}
              </Typography>
            ) : (
              cartError.message
            )}
          </li>
        ))}
      </Box>
    </Alert>
  );
}
